import { API_ENDPOINTS, withParams } from '../config/apis';
import { apiRequest } from '../api/client';

export type CertificateFieldKey =
  | 'PARTICIPANT_NAME'
  | 'BIB_NUMBER'
  | 'GENDER'
  | 'AGE_GROUP'
  | 'GROUP_NAME'
  | 'GUN_TIME_RESULT'
  | 'CHIP_TIME_RESULT'
  | 'GUN_PACE'
  | 'CHIP_PACE'
  | 'RANK'
  | 'GENDER_RANK'
  | 'AGE_GROUP_RANK'
  | 'CATEGORY'
  | 'TOTAL_DISTANCE'
  | 'EVENT_NAME'
  | 'EVENT_DATE'
  | 'EVENT_CITY'
  | 'EVENT_LOCATION'
  | 'HOST_NAME';

export type CertificateFieldCategory = 'EVENT' | 'PARTICIPANT' | 'RECORD';

export const CERTIFICATE_FIELD_CATEGORY_LABELS: Record<CertificateFieldCategory, string> = {
  EVENT: '대회 정보',
  PARTICIPANT: '참가자 정보',
  RECORD: '기록',
};

/** 백엔드 CertificateFieldAlign(Java enum)과 동일하게 대문자로 맞춘다. */
export type CertificateFieldAlign = 'LEFT' | 'CENTER' | 'RIGHT';

export type CertificateTemplateField = {
  key: CertificateFieldKey;
  /** 이미지 원본 대비 텍스트 박스 좌상단 x 위치 (0~100, %) */
  xPercent: number;
  /** 이미지 원본 대비 텍스트 박스 좌상단 y 위치 (0~100, %) */
  yPercent: number;
  /** 이미지 원본 대비 텍스트 박스 너비 (0~100, %) */
  widthPercent: number;
  /** 폰트 크기(pt). 96dpi 기준 1pt = 4/3px이며 소수(0.25 단위)를 허용한다 */
  fontSize: number;
  color: string;
  align: CertificateFieldAlign;
  bold: boolean;
  /** 등록된 폰트(certificate-fonts)의 id. null이면 기본 폰트(나눔고딕). 사용자 폰트는 bold가 무시된다. */
  fontId: number | null;
};

/** 상세 응답 — 백엔드 CertificateTemplateResponse와 동일 필드 */
export type CertificateTemplateSummary = {
  id: number;
  eventId: number;
  eventTitle: string;
  imageUrl: string;
  fields: CertificateTemplateField[];
  createdAt: string;
  updatedAt: string;
};

/** 목록 항목 응답 — 백엔드 CertificateTemplateListItemResponse와 동일 필드 (fields 제외) */
export type CertificateTemplateListItem = {
  id: number;
  eventId: number;
  eventTitle: string;
  imageUrl: string;
  createdAt: string;
  updatedAt: string;
};

/** 생성 요청 본문 — 백엔드 CertificateTemplateCreateRequest */
export type CertificateTemplateCreateRequest = {
  eventId: number;
  fields?: CertificateTemplateField[];
};

/** 수정 요청 본문 — 백엔드 CertificateTemplateUpdateRequest (eventId 없음, 변경 불가) */
export type CertificateTemplateUpdateRequest = {
  fields?: CertificateTemplateField[];
};

export const CERTIFICATE_FIELD_DEFS: {
  key: CertificateFieldKey;
  label: string;
  sampleText: string;
  category: CertificateFieldCategory;
}[] = [
  // 대회 정보
  { key: 'EVENT_NAME', label: '대회명', sampleText: '0000 Runity 마라톤', category: 'EVENT' },
  { key: 'EVENT_DATE', label: '개최일', sampleText: '2026-01-01', category: 'EVENT' },
  { key: 'EVENT_CITY', label: '개최 도시', sampleText: '서울', category: 'EVENT' },
  { key: 'EVENT_LOCATION', label: '개최 장소', sampleText: '올림픽공원', category: 'EVENT' },
  { key: 'HOST_NAME', label: '주최', sampleText: '무토 스포츠', category: 'EVENT' },
  { key: 'CATEGORY', label: '종목', sampleText: '풀코스', category: 'EVENT' },
  { key: 'TOTAL_DISTANCE', label: '총 거리', sampleText: '42.195km', category: 'EVENT' },

  // 참가자 정보
  { key: 'PARTICIPANT_NAME', label: '참가자 이름', sampleText: '홍길동', category: 'PARTICIPANT' },
  { key: 'BIB_NUMBER', label: '배번', sampleText: '1234', category: 'PARTICIPANT' },
  { key: 'GENDER', label: '성별', sampleText: '남자', category: 'PARTICIPANT' },
  { key: 'AGE_GROUP', label: '연령대', sampleText: '30대', category: 'PARTICIPANT' },
  { key: 'GROUP_NAME', label: '그룹', sampleText: 'A조', category: 'PARTICIPANT' },

  // 기록
  { key: 'GUN_TIME_RESULT', label: '건타임 완주기록', sampleText: '00:00:00', category: 'RECORD' },
  { key: 'CHIP_TIME_RESULT', label: '칩타임 완주기록', sampleText: '00:00:00', category: 'RECORD' },
  { key: 'GUN_PACE', label: '건타임 페이스', sampleText: "5'30\"/km", category: 'RECORD' },
  { key: 'CHIP_PACE', label: '칩타임 페이스', sampleText: "5'25\"/km", category: 'RECORD' },
  { key: 'RANK', label: '종합 순위', sampleText: '000위', category: 'RECORD' },
  { key: 'GENDER_RANK', label: '성별 순위', sampleText: '000위', category: 'RECORD' },
  { key: 'AGE_GROUP_RANK', label: '연령대 순위', sampleText: '000위', category: 'RECORD' },
];

export function getCertificateFieldLabel(key: CertificateFieldKey) {
  return CERTIFICATE_FIELD_DEFS.find((def) => def.key === key)?.label ?? key;
}

export function getCertificateFieldSampleText(key: CertificateFieldKey) {
  return CERTIFICATE_FIELD_DEFS.find((def) => def.key === key)?.sampleText ?? key;
}

// 백엔드는 생성/수정을 multipart 로 받음: 'template'(JSON 파트) + 'image'(파일 파트)
function toTemplateFormData(payload: object, image?: File | null) {
  const form = new FormData();
  // @RequestPart('template') 가 JSON 으로 역직렬화되도록 Blob 의 type 을 명시
  form.append('template', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
  if (image) {
    form.append('image', image);
  }
  return form;
}

export const certificateTemplateService = {
  list: () =>
    apiRequest<{ templates: CertificateTemplateListItem[] }>(API_ENDPOINTS.certificateTemplateList)
      .then((res) => res.templates),

  detail: (id: number) =>
    apiRequest<CertificateTemplateSummary>(
      withParams(API_ENDPOINTS.certificateTemplateDetail, { certificateTemplateId: id })
    ),

  create: (body: CertificateTemplateCreateRequest, image: File) =>
    apiRequest<CertificateTemplateSummary, FormData>(API_ENDPOINTS.certificateTemplateCreate, {
      body: toTemplateFormData(body, image),
    }),

  update: (id: number, body: CertificateTemplateUpdateRequest, image?: File | null) =>
    apiRequest<CertificateTemplateSummary, FormData>(
      withParams(API_ENDPOINTS.certificateTemplateUpdate, { certificateTemplateId: id }),
      {
        body: toTemplateFormData(body, image),
      }
    ),

  delete: (id: number) =>
    apiRequest<void>(
      withParams(API_ENDPOINTS.certificateTemplateDelete, { certificateTemplateId: id })
    ),
};
